import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AnnouncerProvider } from "@/components/a11y/Announcer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ToastContainer } from "@/components/notifications/ToastContainer";
import { Amplify } from "aws-amplify";
import { env } from "@/config/env";
import { useAuthStore } from "@/stores/authStore";
import { LoginPage } from "@/features/auth/components/LoginPage";
import { MagicLinkPage } from "@/features/auth/MagicLinkPage";
import { MainLayout } from "@/app/MainLayout";
import { AdminPage } from "@/features/admin/AdminPage";

// Configure Amplify when using real Cognito (staging/prod)
if (!env.isDev) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.aws.cognito.userPoolId,
        userPoolClientId: env.aws.cognito.userPoolClientId,
      },
    },
  });
}

export function App() {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    // Both dev and prod: check for an existing session on mount.
    // Dev mode uses the local auth mock (localStorage-persisted).
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="cx-skeleton flex h-full">
        {/* Sidebar skeleton */}
        <div className="cx-skeleton-sidebar">
          <div className="cx-skeleton-bar cx-skeleton-bar--lg" />
          <div className="cx-skeleton-bar" />
          <div className="cx-skeleton-bar" />
        </div>
        {/* Chat area skeleton */}
        <div className="cx-skeleton-main">
          <div className="cx-skeleton-messages">
            <div className="cx-skeleton-bar cx-skeleton-bar--xl" />
            <div className="cx-skeleton-bar cx-skeleton-bar--md" />
          </div>
          <div className="cx-skeleton-input" />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AnnouncerProvider>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
            }
          />
          <Route path="/auth" element={<MagicLinkPage />} />
          <Route
            path="/admin"
            element={
              isAuthenticated ? <AdminPage /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/*"
            element={
              isAuthenticated ? <MainLayout /> : <Navigate to="/login" replace />
            }
          />
        </Routes>
        <ToastContainer />
      </AnnouncerProvider>
    </ErrorBoundary>
  );
}
